/**
 * CommunicationTraceDashboard — role-gated Communication Trace view
 * (ODE-P10-T07, REQ-099).
 *
 * Consumes the ODE-P10-T06 versioned read endpoints through the gateway port
 * (thin view-model only — no client-side aggregation). Defense in depth:
 * the shell route guard (RequirePermission, client mirror) gates the module,
 * and the T06 backend permission checks remain the authority (401/403/503
 * fail-closed). Detail/search/export are gated by an approved purpose code +
 * case/reference (REQ-101) and audited server-side (REQ-102). Coverage
 * indicators come strictly from the T06 coverage state with visible gaps
 * (REQ-109). REQ-110 framing: neutral lead language, disclaimer + purpose/case
 * context visible; no wrongdoing assertions or verdicts anywhere.
 *
 * The trace access token (T06 session plane) is entered by the investigator
 * and kept in memory only — never persisted, never logged. The staff→trace
 * token bridge is a documented later backend task; until then the module is
 * fail-closed without a token.
 *
 * Traces to: ODE-PRD-REQ-098, ODE-PRD-REQ-099, ODE-PRD-REQ-100,
 *            ODE-PRD-REQ-101, ODE-PRD-REQ-102, ODE-PRD-REQ-109,
 *            ODE-PRD-REQ-110.
 */

import { useState } from 'react';
import type { ModuleDefinition } from '../rbac/catalog.js';
import { StateView } from '../components/StateView.js';
import { traceGateway } from './gateway.js';
import { useCommunicationTraceDashboard } from './useCommunicationTraceDashboard.js';
import { PurposeCaseGate } from './PurposeCaseGate.js';
import { CoverageIndicator } from './CoverageIndicator.js';
import {
  APPROVED_PURPOSE_CODES,
  type CommunicationTraceSummary,
  type ContactFrequencyEntry,
  type TraceActionState,
} from './types.js';

function formatDate(iso: string | null): string {
  if (iso === null) return '—';
  return new Date(iso).toISOString().slice(0, 16).replace('T', ' ');
}

export function CommunicationTraceDashboard({
  module,
}: {
  readonly module: ModuleDefinition;
}): JSX.Element {
  const {
    token,
    setToken,
    driverId,
    setDriverId,
    filters,
    updateFilters,
    summaryState,
    loadSummary,
    detailState,
    requestDetail,
    cancelDetail,
    searchState,
    requestSearch,
    cancelSearch,
    exportState,
    requestExport,
    cancelExport,
    confirmPurposeCase,
  } = useCommunicationTraceDashboard(traceGateway);

  const [searchQuery, setSearchQuery] = useState('');

  const summary = summaryState.kind === 'ready' ? summaryState.summary : null;

  const filteredContacts = (summary?.contacts ?? []).filter((contact) => {
    if (filters.source !== 'ALL' && contact.provenance.sourceKind !== filters.source) return false;
    const needle = filters.contact.trim().toLowerCase();
    if (needle === '') return true;
    return (
      (contact.displayName ?? '').toLowerCase().includes(needle) ||
      contact.contactKey.toLowerCase().includes(needle)
    );
  });

  const filteredDirections = (summary?.calls.byDirection ?? []).filter(
    (entry) => filters.direction === 'ALL' || entry.direction === filters.direction,
  );

  return (
    <section aria-labelledby={`module-${module.id}`} className="module trace-dashboard">
      <h2 id={`module-${module.id}`}>{module.title}</h2>
      <p className="module-description">{module.description}</p>

      {/* Trace session gate — token in memory only (T06 session plane). */}
      <section className="trace-session" aria-label="Sesi akses trace">
        <label className="purpose-field">
          Token akses trace (sesi T06; hanya di memori)
          <input
            data-testid="trace-token"
            type="password"
            value={token}
            onChange={(event) => setToken(event.target.value)}
            placeholder="token sesi investigator"
            autoComplete="off"
          />
        </label>
        <p className="gate-note">
          Token sesi trace (plane sesi T06) dimasukkan investigator dan hanya disimpan di memori.
          Tanpa token, modul gagal tertutup — tidak ada data yang dimuat.
        </p>
      </section>

      {/* Scope + window filters (window maps to the T06 query param). */}
      <section className="trace-filters" aria-label="Filter ringkasan">
        <label className="purpose-field">
          Driver ID
          <input
            data-testid="trace-driver-id"
            value={driverId}
            onChange={(event) => setDriverId(event.target.value)}
            placeholder="mis. driver-1001"
          />
        </label>
        <label className="purpose-field">
          Jendela waktu
          <select
            data-testid="trace-window"
            value={filters.window}
            onChange={(event) =>
              updateFilters({ window: event.target.value as typeof filters.window })
            }
          >
            <option value="7d">7 hari</option>
            <option value="30d">30 hari</option>
            <option value="90d">90 hari</option>
            <option value="custom">Kustom</option>
          </select>
        </label>
        {filters.window === 'custom' && (
          <>
            <label className="purpose-field">
              Mulai (ISO)
              <input
                data-testid="trace-window-start"
                value={filters.start}
                onChange={(event) => updateFilters({ start: event.target.value })}
                placeholder="2026-09-01T00:00:00.000Z"
              />
            </label>
            <label className="purpose-field">
              Selesai (ISO)
              <input
                data-testid="trace-window-end"
                value={filters.end}
                onChange={(event) => updateFilters({ end: event.target.value })}
                placeholder="2026-09-08T00:00:00.000Z"
              />
            </label>
          </>
        )}
        <button type="button" data-testid="trace-load-summary" onClick={loadSummary}>
          Muat ringkasan
        </button>
      </section>

      {/* Summary flow states */}
      {summaryState.kind === 'pending' && (
        <StateView state={{ kind: 'pending' }} />
      )}
      {summaryState.kind === 'loading' && <StateView state={{ kind: 'loading' }} />}
      {summaryState.kind === 'unauthorized' && <StateView state={{ kind: 'unauthorized' }} />}
      {summaryState.kind === 'blocked' && (
        <StateView state={{ kind: 'blocked', message: summaryState.message }} onRetry={loadSummary} />
      )}
      {summaryState.kind === 'unavailable' && (
        <StateView state={{ kind: 'error', message: summaryState.message }} onRetry={loadSummary} />
      )}
      {summaryState.kind === 'error' && (
        <StateView state={{ kind: 'error', message: summaryState.message }} onRetry={loadSummary} />
      )}
      {summaryState.kind === 'empty' && (
        <>
          <StateView state={{ kind: 'empty', payload: null }} onRetry={loadSummary} />
          <CoverageIndicator coverage={summaryState.summary.coverage} />
          <AnalyticsBoundary statement={summaryState.summary.analyticsBoundary} />
        </>
      )}

      {summary !== null && (
        <>
          <CoverageIndicator coverage={summary.coverage} />

          {/* Client-side view filters over the T06 payload (thin view-model). */}
          <section className="trace-filters" aria-label="Filter tampilan">
            <label className="purpose-field">
              Sumber
              <select
                data-testid="trace-source-filter"
                value={filters.source}
                onChange={(event) =>
                  updateFilters({ source: event.target.value as typeof filters.source })
                }
              >
                <option value="ALL">Semua sumber</option>
                <option value="CONTACT_SNAPSHOT">Kontak</option>
                <option value="CALL_METADATA">Panggilan</option>
                <option value="WHATSAPP_NOTIFICATION">Notifikasi WhatsApp</option>
              </select>
            </label>
            <label className="purpose-field">
              Arah panggilan
              <select
                data-testid="trace-direction-filter"
                value={filters.direction}
                onChange={(event) =>
                  updateFilters({ direction: event.target.value as typeof filters.direction })
                }
              >
                <option value="ALL">Semua arah</option>
                <option value="INCOMING">Masuk</option>
                <option value="OUTGOING">Keluar</option>
                <option value="MISSED">Tidak terjawab</option>
                <option value="OTHER">Lainnya</option>
              </select>
            </label>
            <label className="purpose-field">
              Cari kontak
              <input
                data-testid="trace-contact-filter"
                value={filters.contact}
                onChange={(event) => updateFilters({ contact: event.target.value })}
                placeholder="nama / key kontak"
              />
            </label>
          </section>

          <SummarySections
            summary={summary}
            filteredContacts={filteredContacts}
            filteredDirections={filteredDirections}
            onOpenDetail={requestDetail}
          />

          {/* Search (purpose/case-gated). */}
          <section className="trace-action" aria-label="Pencarian kontak">
            <h4>Cari kontak</h4>
            <label className="purpose-field">
              Kata kunci
              <input
                data-testid="trace-search-q"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="nama / key kontak"
              />
            </label>
            <button
              type="button"
              data-testid="trace-search-open"
              disabled={searchQuery.trim() === ''}
              onClick={() => requestSearch(searchQuery.trim())}
            >
              Cari (butuh kode tujuan)
            </button>
            <ActionPanel
              state={searchState}
              actionLabel="Pencarian kontak"
              onConfirm={confirmPurposeCase}
              onCancel={cancelSearch}
              renderReady={(payload) => (
                <div data-testid="search-results">
                  <p>
                    {payload.resultCount} hasil untuk “{payload.query}”
                    {payload.truncated ? ' (dipotong)' : ''}
                  </p>
                  <ul>
                    {payload.results.map((result) => (
                      <li key={result.contactKey} data-testid="search-result">
                        {result.displayName ?? result.contactKey} — {result.observationCount} observasi
                        {result.identityAmbiguity ? ' (identitas ambigu)' : ''}
                      </li>
                    ))}
                  </ul>
                  <AnalyticsBoundary statement={payload.analyticsBoundary} />
                </div>
              )}
            />
          </section>

          {/* Export (purpose/case-gated). */}
          <section className="trace-action" aria-label="Ekspor">
            <h4>Ekspor bundel ringkasan</h4>
            <button type="button" data-testid="trace-export-open" onClick={requestExport}>
              Ekspor (butuh kode tujuan)
            </button>
            <ActionPanel
              state={exportState}
              actionLabel="Ekspor bundel ringkasan"
              onConfirm={confirmPurposeCase}
              onCancel={cancelExport}
              renderReady={(payload) => (
                <div data-testid="export-result">
                  <p>
                    Bundel diekspor untuk {payload.driverId} (tujuan: {payload.purposeCode}
                    {payload.caseRef !== null ? `, kasus: ${payload.caseRef}` : ''}) oleh{' '}
                    {payload.exportedBy} pada {formatDate(payload.generatedAt)}.
                  </p>
                  <AnalyticsBoundary statement={payload.analyticsBoundary} />
                </div>
              )}
            />
          </section>
        </>
      )}

      {/* Contact detail (purpose/case-gated). */}
      {detailState.kind !== 'idle' && (
        <section className="trace-action" aria-label="Detail kontak">
          <ActionPanel
            state={detailState}
            actionLabel="Detail kontak"
            onConfirm={confirmPurposeCase}
            onCancel={cancelDetail}
            renderReady={(payload) => (
              <div data-testid="contact-detail">
                <h4>{payload.contact.displayName ?? payload.contact.contactKey}</h4>
                <p>
                  Key: {payload.contact.contactKey} · {payload.contact.observationCount} observasi ·
                  pertama {formatDate(payload.contact.firstObservedAt)} · terakhir{' '}
                  {formatDate(payload.contact.lastObservedAt)}
                  {payload.contact.identityAmbiguity ? ' · identitas ambigu (tidak digabung)' : ''}
                </p>
                <p>
                  Panggilan terkait: {payload.relatedCalls.totalCount} (total{' '}
                  {payload.relatedCalls.duration.totalSeconds}s, median{' '}
                  {payload.relatedCalls.duration.medianSeconds ?? '—'}s)
                </p>
                <AnalyticsBoundary statement={payload.analyticsBoundary} />
              </div>
            )}
          />
        </section>
      )}
    </section>
  );
}

function SummarySections({
  summary,
  filteredContacts,
  filteredDirections,
  onOpenDetail,
}: {
  readonly summary: CommunicationTraceSummary;
  readonly filteredContacts: readonly ContactFrequencyEntry[];
  readonly filteredDirections: readonly { direction: string; count: number }[];
  readonly onOpenDetail: (contactKey: string) => void;
}): JSX.Element {
  return (
    <>
      <section className="trace-section" aria-label="Ringkasan kontak">
        <h4>Kontak (frekuensi, jendela {summary.window.label})</h4>
        {filteredContacts.length === 0 ? (
          <p data-testid="contacts-empty">Tidak ada kontak dalam filter ini.</p>
        ) : (
          <table data-testid="contacts-table">
            <thead>
              <tr>
                <th>Nama</th>
                <th>Key</th>
                <th>Observasi</th>
                <th>Terakhir</th>
                <th>Sumber</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map((contact) => (
                <tr key={contact.contactKey} data-testid="contact-row">
                  <td>{contact.displayName ?? '—'}</td>
                  <td>{contact.contactKey}</td>
                  <td>{contact.observationCount}</td>
                  <td>{formatDate(contact.lastObservedAt)}</td>
                  <td>{contact.provenance.sourceKind}</td>
                  <td>
                    <button
                      type="button"
                      data-testid={`contact-detail-${contact.contactKey}`}
                      onClick={() => onOpenDetail(contact.contactKey)}
                    >
                      Detail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="trace-section" aria-label="Ringkasan panggilan">
        <h4>Panggilan (metadata, jendela {summary.window.label})</h4>
        <p data-testid="calls-total">
          Total {summary.calls.totalCount} · durasi total {summary.calls.duration.totalSeconds}s ·
          median {summary.calls.duration.medianSeconds ?? '—'}s
        </p>
        <ul data-testid="calls-directions">
          {filteredDirections.map((entry) => (
            <li key={entry.direction} data-testid={`calls-direction-${entry.direction}`}>
              {entry.direction}: {entry.count}
            </li>
          ))}
        </ul>
      </section>

      <section className="trace-section" aria-label="Ringkasan WhatsApp">
        <h4>Notifikasi WhatsApp teramati (jendela {summary.window.label})</h4>
        <p data-testid="whatsapp-total">Total {summary.whatsapp.totalNotificationCount} notifikasi</p>
        {summary.whatsapp.activityByDay.length > 0 && (
          <ul data-testid="whatsapp-activity">
            {summary.whatsapp.activityByDay.map((entry) => (
              <li key={entry.day} data-testid={`whatsapp-day-${entry.day}`}>
                {entry.day}: {entry.notificationCount}
              </li>
            ))}
          </ul>
        )}
      </section>

      <AnalyticsBoundary statement={summary.analyticsBoundary} />
    </>
  );
}

function AnalyticsBoundary({ statement }: { readonly statement: string }): JSX.Element {
  return (
    <p className="analytics-boundary" data-testid="analytics-boundary">
      {statement}
    </p>
  );
}

function ActionPanel<T>({
  state,
  actionLabel,
  onConfirm,
  onCancel,
  renderReady,
}: {
  readonly state: TraceActionState<T>;
  readonly actionLabel: string;
  readonly onConfirm: (purposeCode: (typeof APPROVED_PURPOSE_CODES)[number], caseRef: string) => void;
  readonly onCancel: () => void;
  readonly renderReady: (payload: T) => JSX.Element;
}): JSX.Element | null {
  if (state.kind === 'idle') return null;
  if (state.kind === 'gated') {
    return <PurposeCaseGate actionLabel={actionLabel} onConfirm={onConfirm} onCancel={onCancel} />;
  }
  if (state.kind === 'loading') return <StateView state={{ kind: 'loading' }} />;
  if (state.kind === 'unauthorized') return <StateView state={{ kind: 'unauthorized' }} />;
  if (state.kind === 'blocked') return <StateView state={{ kind: 'blocked', message: state.message }} />;
  if (state.kind === 'unavailable') return <StateView state={{ kind: 'error', message: state.message }} />;
  if (state.kind === 'error') return <StateView state={{ kind: 'error', message: state.message }} />;
  return renderReady(state.payload);
}